import React from 'react';
import { Flex } from 'rebass';
import { Input, Button } from '@lomadee/design-system';
import { Formik } from 'formik';
import MaskedInput from '@plug/components/masked-input';
import { useCreateOrganizationContext } from '@plug/contexts/CreateOrganizationContext';
import { OrganizationRegisterOrganizationDataSchema } from './OrganizationRegistrationOrganizationData.schema';
import {
  verifyOrganizationNameMutation,
  verifyOrganizationNameVariables,
  VERIFY_ORGANIZATION_NAME,
} from '@plug/graphql/queries/verify-organization-name';
import { useLazyQuery } from '@apollo/client';
import { Spacer } from '@plug/ui';
import { TitleForm } from '../../../affiliate-registration/style';
import { categoryOptions } from '@plug/domains/onboarding/components/step-customize';
import { Trans, useTranslation } from 'react-i18next';

const OrganizationRegistrationOrganizationDataStep: React.FC = () => {
  const { values: contextValues, setStep, addValues } = useCreateOrganizationContext();
  const { t } = useTranslation();

  const [
    verifyOrganizationName,
    { loading: loadingVerifyOrganizationName, data: organizationNameData },
  ] = useLazyQuery<verifyOrganizationNameMutation, verifyOrganizationNameVariables>(
    VERIFY_ORGANIZATION_NAME,
  );

  const verifyOrganizationNameExists = async (organizationName: string): Promise<boolean> => {
    await verifyOrganizationName({
      variables: {
        input: {
          name: organizationName,
        },
      },
    });

    return organizationNameData?.verifyOrganizationName ?? false;
  };

  const handleSubmitFormik = (formikValues: CreateOrganizationFormI): void => {
    addValues(formikValues);
    setStep('ADDRESS_DATA');
  };

  const handleBlurInputOrganizationName = async (
    organization_name: string,
    setFieldError: (field: string, value: string) => void,
  ): Promise<boolean> => {
    if (organization_name.length < 3) return false;

    let response = true;
    try {
      const verification = await verifyOrganizationNameExists(organization_name);
      response = verification;
    } catch (error) {
      response = true;
    }

    if (response)
      setFieldError('organization_name', 'Este nome de organização já esta em uso. Tente outro!');

    return response;
  };

  const handleBackToPersonalDataStep = (): void => {
    setStep('PERSONAL_DATA');
  };

  return (
    <Flex flexDirection="column" sx={{ gap: '32px' }}>
      <Spacer mt="32px" />

      <TitleForm>Agora conte um pouco sobre a empresa.</TitleForm>

      <Formik
        initialValues={contextValues}
        validationSchema={OrganizationRegisterOrganizationDataSchema}
        isInitialValid={false}
        onSubmit={handleSubmitFormik}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          handleBlur,
          errors,
          isValid,
          touched,
          setFieldError,
          setFieldValue,
        }): JSX.Element => (
          <>
            <Input
              label={t('hubly-dashboard.signup.admin.right-side.third-step.form.name.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.third-step.form.name.placeholder',
              )}
              name="organization_name"
              value={values.organization_name}
              onChange={handleChange}
              onBlur={(e): void => {
                handleBlur(e);
                handleBlurInputOrganizationName(values.organization_name, setFieldError);
              }}
              error={touched.organization_name && !!errors.organization_name}
              helperText={touched.organization_name ? errors.organization_name : ''}
              disabled={loadingVerifyOrganizationName}
              fluid
            />

            <Input
              label={t('hubly-dashboard.signup.admin.right-side.third-step.form.email.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.third-step.form.email.placeholder',
              )}
              name="contact_email"
              value={values.contact_email}
              onChange={handleChange}
              onBlur={(e): void => {
                handleBlur(e);
                handleBlurInputOrganizationName(values.contact_email, setFieldError);
              }}
              error={touched.contact_email && !!errors.contact_email}
              helperText={touched.contact_email ? errors.contact_email : ''}
              fluid
            />

            <MaskedInput
              mask="99.999.999/9999-99"
              label={t(
                'hubly-dashboard.signup.admin.right-side.third-step.form.document-cnpj.label',
              )}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.third-step.form.document-cnpj.placeholder',
              )}
              name="cnpj"
              value={values.cnpj}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.cnpj && !!errors.cnpj}
              helperText={touched.cnpj ? errors.cnpj : ''}
              fluid
            />

            <MaskedInput
              mask="(99) 99999-9999"
              label={t('hubly-dashboard.signup.admin.right-side.third-step.form.phone.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.third-step.form.phone.placeholder',
              )}
              name="organization_phone"
              value={values.organization_phone}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.organization_phone && !!errors.organization_phone}
              helperText={touched.organization_phone ? errors.organization_phone : ''}
              fluid
            />

            <Input
              onClickDropdownItem={clickedValue => setFieldValue('organization_type', clickedValue)}
              dropdown={categoryOptions}
              label={t('hubly-dashboard.signup.admin.right-side.third-step.form.segment.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.third-step.form.segment.placeholder',
              )}
              name="organization_type"
              value={values.organization_type}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.organization_type && !!errors.organization_type}
              helperText={touched.organization_type ? errors.organization_type : ''}
              fluid
            />

            <Button type="submit" disabled={!isValid} onClick={(): void => handleSubmit()}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.confirm.text')}
            </Button>

            <Button hierarchy="secondary" onClick={handleBackToPersonalDataStep}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.back.text')}
            </Button>
          </>
        )}
      </Formik>
    </Flex>
  );
};

export default OrganizationRegistrationOrganizationDataStep;
